import {
  Box,
  Card,
  CardActions,
  CardContent,
  Divider,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { FormHandles } from '@unform/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LOCAL_STORAGE_KEY_REDIRECT_URL,
  useUserContext,
} from '../../../contexts';
import { ICreateUser, ILogin, feedback } from '../../../services';
import { Button } from '../../MUI/button/Button';
import { useGoogleLogin } from '@react-oauth/google';
import { Register } from './components/Register';
import { Login } from './components/Login';
import { useAppThemeContext } from '../../../contexts';

export interface IFormData {
  name: string;
  email?: string;
  phone?: string;
  cpf?: string;
  password: string;
  passwordConfirmation: string;
}

interface ILoginProps {
  children: React.ReactNode;
}

export const LoginPage: React.FC<ILoginProps> = ({ children }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isRegisterPage, setIsRegisterPage] = useState(false);

  const formRef = useRef<FormHandles>(null);
  const { isAuthenticated, login, googleLogin, create } = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();

  // Routes that don't require authentication — bypass login gate
  // Any path that doesn't match a known protected app route is public
  // (this allows /:slug public business page, /appointment walk-in, etc.)
  const PROTECTED_ROUTE_PREFIXES = [
    '/home', '/profile', '/schedule', '/business', '/employee',
    '/create-business',
    '/sconapp-whatsapp',
    '/onboarding', '/notifications',
    '/admin', '/admin/businesses',
    '/invite-employee',
  ];
  const isPublicRoute =
    location.pathname === '/' ||
    location.pathname === '/forgot-password' ||
    location.pathname.startsWith('/reset-password/') ||
    location.pathname.startsWith('/confirm-email/') ||
    // Anything not matching a known protected prefix is public
    !PROTECTED_ROUTE_PREFIXES.some(prefix =>
      location.pathname === prefix || location.pathname.startsWith(prefix + '/')
    );

  const { lgDown } = useAppThemeContext();

  // Routes that have sidebar/navbar menu items — redirectUrl should point to one of these
  const MENUBAR_ROUTES = ['/home', '/profile', '/schedule', '/business', '/employee', '/create-business', '/create-business-trial', '/sconapp-whatsapp', '/invite-employee'];

  const isValidRedirectUrl = (url: string): boolean => {
    return MENUBAR_ROUTES.some(route => url === route || url.startsWith(route + '/'));
  };

  const getRedirectUrl = () => {
    const savedUrl = localStorage.getItem(LOCAL_STORAGE_KEY_REDIRECT_URL);
    if (savedUrl && isValidRedirectUrl(savedUrl)) {
      return savedUrl;
    }
    return '/home';
  };

  const { mutate: mutateLogin, isLoading: isLoadingLogin } = useMutation(
    (user: ILogin) => login(user),
    {
      onSuccess: () => {
        navigate(getRedirectUrl());
      },
      onError: (error: any) => setErrorMessage(error.message.substring(0, 120)),
    }
  );

  const { mutate: mutateGoogleLogin, isLoading: isLoadingGoogleLogin } = useMutation(
    (jwt: string) => googleLogin(jwt),
    {
      onSuccess: () => {
        navigate(getRedirectUrl());
      },
      onError: (error: any) => setErrorMessage(error.message.substring(0, 120)),
    }
  );

  // Google login with People API scope — backend fetches birthday via People API
  const googleLoginWithBirthday = useGoogleLogin({
    flow: 'implicit',
    scope: 'openid email profile https://www.googleapis.com/auth/user.birthday.read',
    onSuccess: (tokenResponse) => {
      mutateGoogleLogin(tokenResponse.access_token);
    },
    onError: () => console.log('Erro ao logar com Google'),
  });

  const { mutate: mutateCreate, isLoading: isLoadingCreate } = useMutation(
    (user: ICreateUser) => create(user),
    {
      onSuccess: () => {
        feedback('Usuário criado', 'success');
        navigate(getRedirectUrl());
      },
      onError: (error: any) => setErrorMessage(error.message.substring(0, 120)),
    }
  );

  const handleClickRegisterPageLogin = useCallback(() => {
    formRef.current?.reset();
    formRef.current?.setErrors({});
    setErrorMessage('');
    setIsRegisterPage(!isRegisterPage);
    setShowPassword(false);
  }, [isRegisterPage]);

  const isLoading = useMemo(() => {
    return isLoadingCreate || isLoadingLogin || isLoadingGoogleLogin;
  }, [isLoadingCreate, isLoadingLogin, isLoadingGoogleLogin]);

  useEffect(() => {
    if (!isAuthenticated && window.location.pathname !== '/') {
      localStorage.setItem(
        LOCAL_STORAGE_KEY_REDIRECT_URL,
        window.location.pathname + window.location.search
      );
    }
  }, [isAuthenticated]);

  if (isAuthenticated || isPublicRoute) return <>{children}</>;

  return (
    <Box
      width='100vw'
      height='100vh'
      display='flex'
      alignItems='center'
      justifyContent='center'
    >
      <Card>
        <Box padding={2} width={lgDown ? 300 : 500}>
          <CardContent>
            {isRegisterPage ? (
              <Register
                errorMessage={errorMessage}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                mutateCreate={mutateCreate}
                isLoadingCreate={isLoading}
              />
            ) : (
              <Login
                errorMessage={errorMessage}
                setErrorMessage={setErrorMessage}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                mutateLogin={mutateLogin}
                isLoadingLogin={isLoading}
              />
            )}
          </CardContent>

          <CardActions sx={{ padding: 0 }}>
            <Box
              display='flex'
              flexDirection='column'
              gap={2}
              flex={1}
              paddingX={2}
            >
              <Button
                label={isRegisterPage ? 'Cadastrar' : 'Logar'}
                variant='contained'
                disabled={isLoading}
                isLoading={isLoading}
                type='submit'
                form={isRegisterPage ? 'register-form' : 'login-form'}
              />

              <Box display='flex' flexDirection='column' justifyContent='center' alignItems='center' gap={2}>
                <Typography>ou continue com</Typography>

                <Button
                  label='Google'
                  variant='outlined'
                  onClick={() => googleLoginWithBirthday()}
                  disabled={isLoading}
                  startIcon={<svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/><path fill="#FF3D00" d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/></svg>}
                >
                  Entrar com Google
                </Button>
              </Box>

              <Divider variant='middle' />

              <Box
                display='flex'
                justifyContent='center'
                alignItems='center'
                gap={2}
              >
                <Typography fontSize={14}>
                  {isRegisterPage ? 'Já' : 'Não'} possui uma conta?
                </Typography>

                <Button
                  label={isRegisterPage ? 'Login' : 'Cadastre-se'}
                  variant='text'
                  size='small'
                  disabled={isLoading}
                  onClick={handleClickRegisterPageLogin}
                />
              </Box>
            </Box>
          </CardActions>
        </Box>
      </Card>
    </Box>
  );
};
